/**
 * Sync Settings Component
 * Detailed sync management UI
 */

import React, { useState, useEffect } from 'react';
import { Download, Trash2, RefreshCw, AlertCircle, ExternalLink, FolderOpen, CheckCircle } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Switch } from 'components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'components/ui/dialog';
import { backupService } from '../../services/backupService';
import { useGoogleDriveSync } from '../../hooks/useGoogleDriveSync';
import { googleDriveService } from '../../services/googleDrive';

interface SyncSettingsProps {
  onClose: () => void;
}

export const SyncSettings: React.FC<SyncSettingsProps> = ({ onClose }) => {
  const {
    isAuthenticated,
    currentUser,
    lastSyncTime,
    isSyncing,
    error,
    autoSyncEnabled,
    signOut,
    syncNow,
    setAutoSync,
    downloadAndRestore,
    deleteCloudBackup,
  } = useGoogleDriveSync();

  const [backupStats, setBackupStats] = useState<{
    totalItems: number;
    categories: Record<string, number>;
    sizeEstimate: string;
  } | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [localSuccess, setLocalSuccess] = useState<string | null>(null);
  const [folderLink, setFolderLink] = useState<string | null>(null);

  useEffect(() => {
    const loadStats = async () => {
      try {
        const backup = await backupService.createBackupSnapshot();
        const stats = backupService.getBackupStats(backup);
        setBackupStats(stats);
      } catch (err) { console.error('[SyncSettings] Failed to load backup stats:', err); }
    };
    const loadLinks = async () => {
      try {
        const folder = await googleDriveService.getBackupFolderLink();
        setFolderLink(folder);
      } catch (err) { console.error('[SyncSettings] Failed to load Drive links:', err); }
    };
    loadStats();
    if (isAuthenticated) loadLinks();
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      const timer = setTimeout(() => onClose(), 100);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, onClose]);

  const handleSync = async () => {
    try {
      setLocalError(null); setLocalSuccess(null);
      await syncNow();
      setLocalSuccess('Data synced successfully!');
      setTimeout(() => setLocalSuccess(null), 3000);
    } catch (err: any) { setLocalError(err.message || 'Sync failed. Please try again.'); }
  };

  const handleRestore = async () => {
    if (!window.confirm('This will restore data from Google Drive and may overwrite local changes. The page will reload after restoration. Continue?')) return;
    try {
      setLocalError(null); setLocalSuccess(null);
      await downloadAndRestore();
    } catch (err: any) { setLocalError(err.message || 'Restore failed. Please try again.'); }
  };

  const handleDownload = async () => {
    try {
      setLocalError(null); setLocalSuccess(null);
      await googleDriveService.downloadBackupAsFile();
      setLocalSuccess('Backup file downloaded successfully!');
      setTimeout(() => setLocalSuccess(null), 3000);
    } catch (err: any) { setLocalError(err.message || 'Download failed. Please try again.'); }
  };

  const handleViewInDrive = () => { if (folderLink) window.open(folderLink, '_blank'); };

  const handleDelete = async () => {
    if (!showDeleteConfirm) { setShowDeleteConfirm(true); return; }
    try {
      setLocalError(null); setLocalSuccess(null);
      await deleteCloudBackup();
      setShowDeleteConfirm(false);
      setLocalSuccess('Cloud backup deleted successfully.');
      setTimeout(() => setLocalSuccess(null), 3000);
    } catch (err: any) { setLocalError(err.message || 'Delete failed. Please try again.'); }
  };

  const handleSignOut = async () => {
    try { setLocalError(null); setLocalSuccess(null); await signOut(); }
    catch (err: any) { setLocalError(err.message || 'Sign out failed. Please try again.'); }
  };

  const formatDate = (isoString: string | null) => {
    if (!isoString) return 'Never';
    const date = new Date(isoString);
    return date.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  const displayError = error || localError;

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-[600px] max-h-[90vh] p-0 gap-0 overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <DialogHeader className="px-6 py-6 border-b border shrink-0">
          <DialogTitle className="text-2xl font-semibold text-foreground-strong">Google Drive Sync Settings</DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 p-6 overflow-y-auto">
          {displayError && (
            <div className="flex items-center gap-3 p-3 px-4 bg-red-50 border-red-200 rounded-lg text-red-900 mb-5">
              <AlertCircle size={16} className="shrink-0" />
              <span>{displayError}</span>
            </div>
          )}
          {localSuccess && (
            <div className="flex items-center gap-3 p-3 px-4 bg-green-50 border-green-200 rounded-lg text-green-900 mb-5">
              <CheckCircle size={16} className="shrink-0" />
              <span>{localSuccess}</span>
            </div>
          )}

          <div className="mb-8">
            <h3 className="m-0 mb-4 text-base font-semibold text-foreground-strong">Account</h3>
            {currentUser && (
              <div className="flex items-center gap-3 mb-4 p-3 bg-muted rounded-lg">
                {currentUser.picture && <img src={currentUser.picture} alt={currentUser.name} className="w-12 h-12 rounded-full" />}
                <div>
                  <div className="font-semibold text-foreground-strong mb-1">{currentUser.name}</div>
                  <div className="text-sm text-foreground-muted">{currentUser.email}</div>
                </div>
              </div>
            )}
            <Button variant="outline" onClick={handleSignOut} disabled={isSyncing}>Sign Out</Button>
          </div>

          <div className="mb-8">
            <h3 className="m-0 mb-4 text-base font-semibold text-foreground-strong">Auto-Sync</h3>
            <label className="flex items-center gap-2 cursor-pointer text-foreground-strong">
              <Switch id="auto-sync" name="auto-sync" checked={autoSyncEnabled} onCheckedChange={setAutoSync} />
              <span>Automatically sync changes (30 seconds after edits)</span>
            </label>
          </div>

          <div className="mb-8">
            <h3 className="m-0 mb-4 text-base font-semibold text-foreground-strong">Sync Status</h3>
            <div className="flex justify-between items-center py-2 border-b border">
              <span className="font-medium text-foreground-muted">Last Synced:</span>
              <span className="text-foreground-strong">{formatDate(lastSyncTime)}</span>
            </div>
            {isSyncing && (
              <div className="flex items-center gap-2 p-3 bg-muted rounded-lg text-primary mt-3">
                <RefreshCw size={16} className="animate-spin" />
                <span>Syncing...</span>
              </div>
            )}
          </div>

          {backupStats && (
            <div className="mb-8">
              <h3 className="m-0 mb-4 text-base font-semibold text-foreground-strong">Backup Information</h3>
              <div className="flex justify-between items-center py-2 border-b border">
                <span className="font-medium text-foreground-muted">Total Items:</span>
                <span className="text-foreground-strong">{backupStats.totalItems}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border">
                <span className="font-medium text-foreground-muted">Estimated Size:</span>
                <span className="text-foreground-strong">{backupStats.sizeEstimate}</span>
              </div>
              {folderLink && (
                <div className="flex justify-between items-center py-2">
                  <span className="font-medium text-foreground-muted">Location:</span>
                  <button className="inline-flex items-center gap-1.5 bg-transparent border-none text-primary cursor-pointer text-sm p-0 underline hover:text-primary" onClick={handleViewInDrive}>
                    <FolderOpen size={14} />
                    EIR Health Backup folder
                    <ExternalLink size={12} />
                  </button>
                </div>
              )}
              <div className="mt-3">
                {Object.entries(backupStats.categories).map(([category, count]) => (
                  <div key={category} className="flex justify-between py-1.5 text-sm">
                    <span className="text-foreground-muted">{formatCategoryName(category)}:</span>
                    <span className="text-foreground-strong font-medium">{count} items</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mb-8">
            <h3 className="m-0 mb-4 text-base font-semibold text-foreground-strong">Actions</h3>
            <div className="flex gap-3 flex-wrap">
              <Button onClick={handleSync} disabled={isSyncing}><RefreshCw size={16} /> Sync Now</Button>
              <Button variant="outline" onClick={handleRestore} disabled={isSyncing}><Download size={16} /> Restore from Cloud</Button>
              <Button variant="outline" onClick={handleDownload} disabled={isSyncing}><Download size={16} /> Download Backup File</Button>
              {folderLink && (
                <Button variant="outline" onClick={handleViewInDrive}><ExternalLink size={16} /> View in Google Drive</Button>
              )}
            </div>
          </div>

          <div>
            <h3 className="m-0 mb-4 text-base font-semibold text-foreground-strong">Danger Zone</h3>
            {showDeleteConfirm ? (
              <div className="p-4 bg-red-50 border-red-200 rounded-lg">
                <p className="m-0 mb-4 text-red-900">Are you sure? This will delete your cloud backup permanently.</p>
                <div className="flex gap-3 flex-wrap">
                  <Button variant="destructive" onClick={handleDelete} disabled={isSyncing}><Trash2 size={16} /> Confirm Delete</Button>
                  <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
                </div>
              </div>
            ) : (
              <Button variant="destructive" onClick={handleDelete} disabled={isSyncing}><Trash2 size={16} /> Delete Cloud Backup</Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

function formatCategoryName(category: string): string {
  return category.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase()).trim();
}
